'use client';

import React, { FC } from 'react';
import { TbChevronRight, TbInfoCircle, TbPhoto, TbVideo } from 'react-icons/tb';

import {
    ActionIcon,
    Badge,
    Box,
    Card,
    Grid,
    GridCol,
    Group,
    Image,
    MantineColor,
    Space,
    Stack,
    Text,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';

import { ScrollingImage } from '@/../marketing/components/scrolling-image/ScrollingImage';
import { Section } from '@/../marketing/components/section/Section';
import { SectionHeader, SectionHeaderProps } from '@/../marketing/components/section/SectionHeader';
import { Feature } from '@/../marketing/data/features';

import cx from './Features.module.css';

const Features: FC<
    {
        actions?: React.ReactNode;
        children: Feature[];
        variant?: 'default' | 'light' | 'dark';
        bg?: MantineColor;
        wrapper?: boolean;
        gutter?: number;
    } & SectionHeaderProps
> = ({
    prefix,
    title,
    titleGradient,
    description,
    actions,
    variant = 'default',
    bg,
    wrapper = true,
    children,
    gutter = 40,
}) => {
    const content = (
        <Stack gap={60}>
            {title && (
                <SectionHeader prefix={prefix} title={title} titleGradient={titleGradient} description={description} />
            )}
            <Grid gap={{ base: 20, md: gutter }}>
                {children.map((feature, i) => (
                    <GridCol key={i} span={feature.span || { base: 12, md: 6, lg: 4 }}>
                        <Item feature={feature} key={feature.title} />
                    </GridCol>
                ))}
            </Grid>
            {actions && (
                <Group gap="md" justify="center">
                    {actions}
                </Group>
            )}
        </Stack>
    );

    return wrapper ? (
        <Section variant={variant} bg={bg}>
            {content}
        </Section>
    ) : (
        content
    );
};

const Item: FC<{
    feature: Feature;
}> = ({ feature }) => {
    const { ref, width, height } = useElementSize();

    return (
        <Card
            component={feature.action ? 'a' : 'div'}
            href={feature.action ? feature.action.href : undefined}
            className={cx.card}
            p={30}
            radius="md"
            withBorder
            data-gradient={feature.gradient}
            ref={ref}
            variant={feature.variant}
            c={feature.variant === 'dark' ? 'white' : undefined}
            bg={feature.color}
            data-orientation={feature.orientation}
        >
            {feature.image && (
                <Card.Section className={cx.image}>
                    <Image src={feature.image.src} />
                </Card.Section>
            )}

            {feature.preview && (
                <Box className={cx.previewTrigger}>
                    <ActionIcon variant="light" color="gray" size="lg" radius="xl">
                        {feature.preview.type === 'video' && <TbVideo size={18} strokeWidth={2} />}
                        {(feature.preview.type === 'image' || feature.preview.type === 'scrolling-image') && (
                            <TbPhoto size={18} strokeWidth={2} />
                        )}
                        {feature.preview.type === 'text' && <TbInfoCircle size={18} strokeWidth={2} />}
                    </ActionIcon>
                </Box>
            )}

            <Stack className={cx.contentWrapper}>
                {feature.preview && (
                    <Box className={cx.preview} bg={feature.variant === 'dark' ? feature.color : 'white'}>
                        {feature.preview.type === 'image' && (
                            <Image
                                src={feature.preview.image.src}
                                width={feature.preview.image.width}
                                height={feature.preview.image.height}
                            />
                        )}
                        {feature.preview.type === 'scrolling-image' && (
                            <ScrollingImage image={feature.preview.image} aspectRatio={height / width} autoplay />
                        )}
                        {feature.preview.type === 'video' && (
                            <video autoPlay muted playsInline loop>
                                {feature.preview.sources.map((source) => (
                                    <source key={source.src} src={source.src} type={source.type} />
                                ))}
                            </video>
                        )}
                        {feature.preview.type === 'text' && (
                            <Box className={cx.previewList}>{feature.preview.text}</Box>
                        )}
                    </Box>
                )}

                <Stack className={cx.content} gap="lg" align="flex-start">
                    {feature.icon ? (
                        <Group w="100%" justify="space-between" align="start">
                            <Box className={cx.icon}>{feature.icon}</Box>
                            {feature.badge && (
                                <Badge radius="sm" color="black">
                                    {feature.badge}
                                </Badge>
                            )}
                        </Group>
                    ) : feature.badge ? (
                        <Badge radius="sm" color="black">
                            {feature.badge}
                        </Badge>
                    ) : null}
                    <Stack gap="sm" w="100%">
                        <Text fw={800} className={cx.title}>
                            {feature.header || feature.title}
                        </Text>
                        <Box fz="lg">{feature.description}</Box>
                    </Stack>
                </Stack>

                {feature.action && (
                    <>
                        <Space h="md" />
                        <Group gap={4} className={cx.learnMore}>
                            {feature.action.label}
                            <TbChevronRight size={14} strokeWidth={2} />
                        </Group>
                    </>
                )}
            </Stack>
        </Card>
    );
};

export { Features };
